import { useMemo, useState, useSyncExternalStore } from 'react';
import { Drawer } from '@mantine/core';
import { BrowserRouter, Navigate, Outlet, Route, Routes } from 'react-router-dom';
import { NavContext } from './components/layout/NavContext';
import { UndoLastActionDialog } from './components/UndoLastAction';
import { Sidebar } from './components/layout/Sidebar';
import layout from './components/layout/layout.module.css';
import { isSidebarCollapsed, subscribeSettings } from './settings';
import { AppStateProvider } from './state';
import { HistoryScreen } from './screens/history/HistoryScreen';
import { UsersScreen } from './screens/users/UsersScreen';
import { ProfileDialog } from './components/ProfileDialog';
import { can } from '@parts/shared';
import { useRole } from './role';
import { PurchaseScreen } from './screens/purchase/PurchaseScreen';
import { RequestScreen } from './screens/request/RequestScreen';
import { RequestsView } from './screens/requests/RequestsView';
import { StatsScreen } from './screens/stats/StatsScreen';
import { StatusPage } from './screens/status/StatusPage';

/** Каркас: боковая панель слева, экран справа. На узком экране панель выезжает по кнопке-гамбургеру. */
function Shell() {
  const [navOpen, setNavOpen] = useState(false);
  const nav = useMemo(() => ({ openNav: () => setNavOpen(true) }), []);
  const collapsed = useSyncExternalStore(subscribeSettings, isSidebarCollapsed);
  return (
    <NavContext.Provider value={nav}>
      <div className={layout.shell} data-collapsed={collapsed || undefined}>
        <aside className={layout.sideWrap}>
          <Sidebar collapsed={collapsed} />
        </aside>
        <main className={layout.main}>
          <Outlet />
        </main>
      </div>
      <UndoLastActionDialog />
      <ProfileDialog />
      <Drawer opened={navOpen} onClose={() => setNavOpen(false)} size={260} withCloseButton={false} padding={0}>
        <div style={{ height: '100dvh' }}>
          <Sidebar onNavigate={() => setNavOpen(false)} />
        </div>
      </Drawer>
    </NavContext.Provider>
  );
}

/** Экран только для администратора (остальных — на «Текущие запросы»). */
function AdminOnly({ children }: { children: React.ReactNode }) {
  const role = useRole();
  return can(role, 'USERS') ? <>{children}</> : <Navigate to="/requests" replace />;
}

/** Экраны приложения: новый запрос, закупка, текущие запросы, статистика, история. */
export function App() {
  return (
    <BrowserRouter>
      <Routes>
        <Route path="/status" element={<StatusPage />} />
        <Route
          element={
            <AppStateProvider>
              <Shell />
            </AppStateProvider>
          }
        >
          <Route path="/" element={<Navigate to="/requests" replace />} />
          <Route path="/request" element={<RequestScreen />} />
          <Route path="/purchase" element={<PurchaseScreen />} />
          <Route path="/requests" element={<RequestsView />} />
          <Route path="/stats" element={<StatsScreen />} />
          <Route path="/history" element={<HistoryScreen />} />
          <Route path="/users" element={<AdminOnly><UsersScreen /></AdminOnly>} />
          <Route path="*" element={<Navigate to="/requests" replace />} />
        </Route>
      </Routes>
    </BrowserRouter>
  );
}
